import { cn } from "@/lib/utils";
import { ButtonProps } from "./types";
import { Spinner } from "../Spinner";

const baseStyles =
  "flex items-center justify-center gap-2 font-medium rounded-xl transition-colors disabled:cursor-not-allowed";

const sizeStyles = {
  xs: "text-xs px-2 py-1",
  sm: "text-sm px-6 py-2.5",
  md: "text-base px-4 py-2",
  lg: "text-lg px-5 py-2.5",
};

const variantColorStyles = {
  default: {
    primary: "bg-[#014fe8] text-white hover:bg-blue-700 disabled:opacity-50",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-300 disabled:hover:bg-gray-300",
    success: "bg-green-600 text-white hover:bg-green-700 disabled:bg-green-300 disabled:hover:bg-green-300",
    warning: "bg-amber-500 text-white hover:bg-amber-600 disabled:bg-amber-300 disabled:hover:bg-amber-300",
    destructive: "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:hover:bg-red-300",
    info: "bg-sky-500 text-white hover:bg-sky-600 disabled:bg-sky-300 disabled:hover:bg-sky-300",
    white:
      "bg-white text-black hover:bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-gray-100",
  },
  outline: {
    primary:
      "border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:border-blue-300 disabled:text-blue-300 disabled:hover:bg-transparent",
    secondary:
      "border border-[rgba(140,141,143,0.4)] text-white hover:bg-gray-500 disabled:border-gray-300 disabled:text-gray-300 disabled:hover:bg-transparent",
    success:
      "border border-green-600 text-green-600 hover:bg-green-50 disabled:border-green-300 disabled:text-green-300 disabled:hover:bg-transparent",
    warning:
      "border border-amber-500 text-amber-500 hover:bg-amber-50 disabled:border-amber-300 disabled:text-amber-300 disabled:hover:bg-transparent",
    destructive:
      "border border-red-600 text-red-600 hover:bg-red-50 disabled:border-red-300 disabled:text-red-300 disabled:hover:bg-transparent",
    info: "border border-sky-500 text-sky-500 hover:bg-sky-50 disabled:border-sky-300 disabled:text-sky-300 disabled:hover:bg-transparent",
    white:
      "border border-[#8c8d8f]/40 text-white hover:bg-[#8c8d8f]/40 disabled:border-gray-300 disabled:text-gray-300 disabled:hover:bg-transparent",
  },
  transparent: {
    primary: "text-blue-600 hover:bg-blue-50 disabled:text-blue-300 disabled:hover:bg-transparent",
    secondary: "text-gray-600 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent",
    success: "text-green-600 hover:bg-green-50 disabled:text-green-300 disabled:hover:bg-transparent",
    warning: "text-amber-500 hover:bg-amber-50 disabled:text-amber-300 disabled:hover:bg-transparent",
    destructive: "text-red-600 hover:bg-red-50  disabled:text-red-300 disabled:hover:bg-transparent",
    info: "text-sky-500 hover:bg-sky-50 disabled:text-sky-300 disabled:hover:bg-transparent",
    white: "text-white hover:bg-white/30 disabled:text-gray-300 disabled:hover:bg-transparent",
  },
  ghost: {
    primary: "bg-[#2b2d31] text-white hover:bg-[#3a3c42] disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    secondary:
      "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-gray-300 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    success:
      "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-green-300 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    warning:
      "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-amber-300 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    destructive:
      "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-red-300 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    info: "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-sky-300 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
    white:
      "bg-[#2b2d31] text-white hover:bg-[#3a3c42] hover:text-gray-100 disabled:bg-[#2b2d31]/50 disabled:text-white/50",
  },
};

export const Button = ({
  size = "sm",
  variant = "default",
  color = "primary",
  className,
  children,
  loading = false,
  disabled,
  ...props
}: ButtonProps) => {
  return (
    <button
      type="button"
      className={cn(baseStyles, sizeStyles[size], variantColorStyles[variant][color], className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner color="currentColor" />}
      {children}
    </button>
  );
};
